const Lenis = require("lenis").default || require("lenis");

let select2Promise = null;

// Deferred/consent-managed scripts may execute after DOMContentLoaded.
function whenDOMReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

function loadSelect2() {
  if (window.jQuery?.fn?.select2) {
    return Promise.resolve();
  }

  if (!select2Promise) {
    // A failed chunk request must not poison every later call, so drop the
    // cached promise and let the next caller try again.
    select2Promise = import("select2").then(
      () => undefined,
      (error) => {
        select2Promise = null;
        throw error;
      },
    );
  }

  return select2Promise;
}

// Check if we're in the WordPress editor/admin
function isWordPressEditor() {
  return (
    typeof window !== "undefined" &&
    (window.location.pathname.includes("/wp-admin/") ||
      window.location.pathname.includes("/wp-admin/post.php") ||
      window.frameElement !== null ||
      document.body.classList.contains("block-editor-page") ||
      document.getElementById("editor") !== null ||
      (typeof wp !== "undefined" && wp.blocks !== undefined))
  );
}

// If we're in the editor, only initialize Select2 and exit
if (isWordPressEditor()) {
  console.log("WordPress editor detected - loading minimal scripts only");

  // Only initialize Select2 for select elements within ACF block previews, not editing fields
  jQuery(document).ready(function ($) {
    loadSelect2().then(() => {
      const selectOptions = {
        minimumResultsForSearch: Infinity,
        placeholder: "Select",
      };

      // Only target select elements inside block previews, not ACF fields
      $(".acf-block-preview select").select2(selectOptions);

      const reinitSelects = () => {
        $(".acf-block-preview select")
          .not(".select2-hidden-accessible")
          .select2(selectOptions);
      };

      // Re-initialize when blocks are updated
      if (window.acf) {
        window.acf.addAction("render_block_preview", function () {
          // Small delay to ensure DOM is ready
          setTimeout(reinitSelects, 100);
        });
      }

      // Inside the WP 7.1 iframed canvas there is no `acf` global, so fall back
      // to the event editor-canvas-init.js dispatches on re-rendered previews.
      document.addEventListener("blockEditorInit", reinitSelects);
    });
  });

  // Exit early - don't load any other frontend scripts
} else {
  // All frontend functionality below - only runs on actual site pages

  // Lenis Smooth Scroll + GSAP ScrollTrigger & Animations
  whenDOMReady(function () {
    const deferredImages = document.querySelectorAll("img[data-v1-lazy-src]");

    const loadDeferredImage = (image) => {
      if (!image?.dataset.v1LazySrc) return;

      image.src = image.dataset.v1LazySrc;
      image.removeAttribute("data-v1-lazy-src");
    };

    if (!("IntersectionObserver" in window)) {
      deferredImages.forEach(loadDeferredImage);
    } else {
      const deferredImageObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            loadDeferredImage(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: "200px 0px" },
      );

      deferredImages.forEach((image) => deferredImageObserver.observe(image));
    }

    // --- Lenis smooth scroll ---
    const isMobile = window.innerWidth < 768;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let lenis = null;

    if (!isMobile && !prefersReducedMotion) {
      lenis = new Lenis({
        lerp: 0.1,
        smoothWheel: true,
        wheelMultiplier: 1,
      });

      // Wire Lenis into GSAP's ticker for a single unified RAF loop
      if (typeof gsap !== "undefined") {
        gsap.ticker.add((time) => {
          lenis.raf(time * 1000);
        });
        gsap.ticker.lagSmoothing(0);
      } else {
        // Fallback RAF loop if GSAP isn't available
        function raf(time) {
          lenis.raf(time);
          requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);
      }
    }

    // Expose for other scripts if needed
    window.lenis = lenis;

    // --- Sticky header ---
    const header = document.querySelector(".header");

    if (header) {
      let headerHeight = 0;
      let isSticky = false;
      let headerHeightUpdatePending = false;

      function updateHeaderHeight() {
        if (headerHeightUpdatePending) return;
        headerHeightUpdatePending = true;

        requestAnimationFrame(() => {
          headerHeight = header.offsetHeight;
          headerHeightUpdatePending = false;
        });
      }

      function updateStickyHeader(scrollY) {
        if (!headerHeight) {
          updateHeaderHeight();
        }

        const showThreshold = headerHeight + 300;
        const hideThreshold = 300;

        if (scrollY > showThreshold) {
          if (!isSticky) {
            header.classList.add("header--sticky");
            header.classList.remove("header--show");

            jQuery(header)
              .stop(true, true)
              .hide()
              .slideDown(280, function () {
                header.classList.add("header--show");
                header.style.display = "";
              });

            isSticky = true;
          }
        } else if (scrollY < hideThreshold) {
          if (isSticky) {
            jQuery(header)
              .stop(true, true)
              .slideUp(220, function () {
                header.classList.remove("header--show");
                header.classList.remove("header--sticky");
                header.style.display = "";
              });

            isSticky = false;
          }
        }
      }

      updateHeaderHeight();

      // Use Lenis scroll event when available, native scroll as fallback
      if (lenis) {
        lenis.on("scroll", (instance) => {
          updateStickyHeader(instance.animatedScroll);
        });
      } else {
        window.addEventListener(
          "scroll",
          () => updateStickyHeader(window.scrollY),
          { passive: true },
        );
      }

      window.addEventListener(
        "resize",
        () => {
          updateHeaderHeight();
          updateStickyHeader(lenis ? lenis.animatedScroll : window.scrollY);
        },
        { passive: true },
      );

      updateStickyHeader(window.scrollY);
    }

    function initHeaderMegaMenus() {
      const megaRoots = Array.from(
        document.querySelectorAll(".header__menu-item--mega[data-mega-root]"),
      );
      const nonMegaTopLevelItems = Array.from(
        document.querySelectorAll(
          ".header__menu > .header__menu-item--top-level:not(.header__menu-item--mega)",
        ),
      );
      const closeDelay = 260;
      const closeAnimationDuration = 220;

      if (!megaRoots.length) {
        return;
      }

      // Must stay in step with the `header-compact` breakpoint in
      // abstracts/_mixins.scss: below it the nav is the off-canvas drawer, so
      // the hover-driven desktop mega menus must not engage.
      const canHover = () =>
        window.innerWidth > 1200 &&
        window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      // The off-canvas drawer layout (`header-compact`), where the lower
      // levels are reached by drilling down rather than by columns.
      const isCompact = () => window.matchMedia("(max-width: 1200px)").matches;
      const headerInner = header?.querySelector(".header__inner") || header;
      const menuPositionContext =
        header?.querySelector(".header__wrapper") || headerInner || header;

      const getTopLink = (root) =>
        Array.from(root.children).find(
          (child) =>
            child.classList &&
            child.classList.contains("header__menu-link--top-level"),
        ) || null;

      const syncHeaderMegaMenuState = () => {
        if (!header) {
          return;
        }

        header.classList.toggle(
          "header--mega-menu-open",
          canHover() &&
            megaRoots.some(
              (root) =>
                root.classList.contains("is-open") ||
                root.classList.contains("is-closing"),
            ),
        );
      };

      const closeAllMenus = (exceptRoot = null, options = {}) => {
        const immediate = options.immediate !== false;

        megaRoots.forEach((root) => {
          if (root === exceptRoot) {
            return;
          }

          if (typeof root._megaMenuClearCloseTimer === "function") {
            root._megaMenuClearCloseTimer();
          }

          if (!immediate && canHover() && typeof root._megaMenuClose === "function") {
            root._megaMenuClose();
          } else if (typeof root._megaMenuForceClose === "function") {
            root._megaMenuForceClose();
          } else {
            root.classList.remove("is-open", "is-closing");
          }

          const topLink = getTopLink(root);

          if (topLink) {
            topLink.setAttribute("aria-expanded", "false");
          }
        });

        syncHeaderMegaMenuState();
      };

      megaRoots.forEach((root) => {
        const megaMenu = root.querySelector("[data-mega-menu]");
        const topLink = getTopLink(root);
        let closeTimerId = 0;
        let closeAnimationTimerId = 0;

        if (!megaMenu) {
          return;
        }

        const secondaryItems = Array.from(
          megaMenu.querySelectorAll("[data-mega-secondary-item]"),
        );
        const secondaryLinks = Array.from(
          megaMenu.querySelectorAll("[data-mega-secondary-link]"),
        );
        const tertiaryItems = Array.from(
          megaMenu.querySelectorAll("[data-mega-tertiary-item]"),
        );
        const quaternaryItems = Array.from(
          megaMenu.querySelectorAll("[data-mega-quaternary-item]"),
        );
        const panels = Array.from(megaMenu.querySelectorAll("[data-mega-panel]"));
        const subpanels = Array.from(
          megaMenu.querySelectorAll("[data-mega-subpanel]"),
        );
        const previews = Array.from(
          megaMenu.querySelectorAll("[data-mega-preview]"),
        );
        const previewContainer = megaMenu.querySelector(".header__mega-preview");
        const previewLink = megaMenu.querySelector("[data-mega-preview-link]");
        const tertiaryContainer = megaMenu.querySelector(".header__mega-tertiary");
        const quaternaryContainer = megaMenu.querySelector(
          ".header__mega-quaternary",
        );
        const megaPanel = megaMenu.querySelector(".header__mega-panel");
        const megaPrimary = megaMenu.querySelector(".header__mega-primary");
        const defaultPanelId = megaMenu.dataset.defaultPanel || "";
        const defaultPreviewId = megaMenu.dataset.defaultPreview || "";

        // The walker ships these images with their URLs parked in data
        // attributes. The panel is display:none until it opens, and Chrome
        // loads a lazy image inside a display:none subtree straight away, so
        // leaving real src attributes on them meant the whole preview set was
        // downloading during the initial page load and taking connections the
        // hero video was waiting on.
        const hydratePreviewImages = () => {
          megaMenu
            .querySelectorAll("img[data-mega-src]")
            .forEach((image) => {
              const srcset = image.getAttribute("data-mega-srcset");
              const sizes = image.getAttribute("data-mega-sizes");

              // srcset and sizes go on first: setting src last is what starts
              // the fetch, and by then the candidate list is complete.
              if (sizes) {
                image.setAttribute("sizes", sizes);
                image.removeAttribute("data-mega-sizes");
              }

              if (srcset) {
                image.setAttribute("srcset", srcset);
                image.removeAttribute("data-mega-srcset");
              }

              image.setAttribute("src", image.getAttribute("data-mega-src"));
              image.removeAttribute("data-mega-src");
            });
        };

        // Warm the set once the page has finished loading so a first hover is
        // never waiting on a cold fetch. Idle time only: the whole point is to
        // stay out of the hero's way.
        const warmPreviewImages = () => {
          if (typeof window.requestIdleCallback === "function") {
            window.requestIdleCallback(hydratePreviewImages, { timeout: 3000 });
            return;
          }

          window.setTimeout(hydratePreviewImages, 1000);
        };

        if (document.readyState === "complete") {
          warmPreviewImages();
        } else {
          window.addEventListener("load", warmPreviewImages, { once: true });
        }

        const findPanel = (panelId) =>
          panels.find((panel) => panel.dataset.megaPanel === panelId) || null;

        const findPreview = (previewId) =>
          previews.find((preview) => preview.dataset.megaPreview === previewId) ||
          null;

        const syncTertiaryPanelHeight = (panelId = "") => {
          if (!tertiaryContainer) {
            return;
          }

          if (!canHover()) {
            tertiaryContainer.style.removeProperty("--mega-tertiary-panel-height");
            return;
          }

          const panel =
            (panelId ? findPanel(panelId) : null) ||
            panels.find((candidate) => candidate.classList.contains("is-active")) ||
            null;
          const tertiaryList = panel?.querySelector(".header__mega-tertiary-list");
          const measuredHeight = tertiaryList
            ? Math.ceil(tertiaryList.getBoundingClientRect().height)
            : panel?.classList.contains("is-empty")
              ? 1
              : 0;

          tertiaryContainer.style.setProperty(
            "--mega-tertiary-panel-height",
            `${Math.max(measuredHeight, 1)}px`,
          );
        };

        const findSubpanel = (subpanelId) =>
          subpanels.find((subpanel) => subpanel.dataset.megaSubpanel === subpanelId) ||
          null;

        // Same approach as the third level: the fourth level panels are
        // absolutely positioned, so the column is sized to the open one.
        const syncQuaternaryPanelHeight = () => {
          if (!quaternaryContainer) {
            return;
          }

          if (!canHover()) {
            quaternaryContainer.style.removeProperty(
              "--mega-quaternary-panel-height",
            );
            return;
          }

          const activeSubpanel = subpanels.find((subpanel) =>
            subpanel.classList.contains("is-active"),
          );
          const quaternaryList = activeSubpanel?.querySelector(
            ".header__mega-quaternary-list",
          );
          const measuredHeight = quaternaryList
            ? Math.ceil(quaternaryList.getBoundingClientRect().height)
            : 0;

          quaternaryContainer.style.setProperty(
            "--mega-quaternary-panel-height",
            `${Math.max(measuredHeight, 1)}px`,
          );
        };

        const setActiveSubpanel = (subpanelId) => {
          const resolvedSubpanelId = findSubpanel(subpanelId) ? subpanelId : "";

          subpanels.forEach((subpanel) => {
            subpanel.classList.toggle(
              "is-active",
              resolvedSubpanelId !== "" &&
                subpanel.dataset.megaSubpanel === resolvedSubpanelId,
            );
          });

          quaternaryItems.forEach((item) => item.classList.remove("is-active"));
          syncQuaternaryPanelHeight();
        };

        // Drawer layout only: which level of the drill-down is showing. 2 is
        // the second level list; 3 and 4 swap it for the active panel.
        const setMobileLevel = (level) => {
          megaMenu.classList.toggle("is-level-3", level === 3);
          megaMenu.classList.toggle("is-level-4", level === 4);
          megaMenu.scrollTop = 0;
        };

        const getMobileLevel = () =>
          megaMenu.classList.contains("is-level-4")
            ? 4
            : megaMenu.classList.contains("is-level-3")
              ? 3
              : 2;

        // The fourth level column only takes up space while the active second
        // level item has a fourth level below it, so other branches keep the
        // full-width columns. The second level column is pinned at its current
        // width while it shows, so its items don't reflow under the pointer.
        const setQuaternaryLayout = (enabled) => {
          if (!megaPanel || !quaternaryContainer) {
            return;
          }

          const isEnabled = enabled && !isCompact();

          if (isEnabled && !megaPanel.classList.contains("is-quaternary-layout")) {
            const primaryWidth = megaPrimary
              ? megaPrimary.getBoundingClientRect().width
              : 0;

            if (primaryWidth > 0) {
              megaPanel.style.setProperty(
                "--mega-secondary-width",
                `${Math.floor(primaryWidth)}px`,
              );
            }
          }

          if (!isEnabled) {
            megaPanel.style.removeProperty("--mega-secondary-width");
          }

          megaPanel.classList.toggle("is-quaternary-layout", isEnabled);
        };

        const syncPanelHeights = () => {
          const activePanel = panels.find((panel) =>
            panel.classList.contains("is-active"),
          );

          syncTertiaryPanelHeight(activePanel?.dataset.megaPanel || "");
          syncQuaternaryPanelHeight();
        };

        const setExpanded = (expanded) => {
          if (topLink) {
            topLink.setAttribute("aria-expanded", expanded ? "true" : "false");
          }
        };

        const clearCloseTimer = () => {
          if (!closeTimerId) {
            return;
          }

          window.clearTimeout(closeTimerId);
          closeTimerId = 0;
        };

        const clearCloseAnimationTimer = () => {
          if (!closeAnimationTimerId) {
            return;
          }

          window.clearTimeout(closeAnimationTimerId);
          closeAnimationTimerId = 0;
        };

        const forceClose = () => {
          clearCloseTimer();
          clearCloseAnimationTimer();
          root.classList.remove("is-open", "is-closing");
          setExpanded(false);
          resetState();
          syncHeaderMegaMenuState();
        };

        const updateMenuOffset = () => {
          if (!headerInner || !menuPositionContext || !canHover()) {
            megaMenu.style.removeProperty("--mega-menu-top-offset");
            return;
          }

          const contextRect = menuPositionContext.getBoundingClientRect();
          const headerInnerRect = headerInner.getBoundingClientRect();
          const topOffset = Math.max(
            0,
            Math.round(headerInnerRect.bottom - contextRect.top),
          );

          megaMenu.style.setProperty("--mega-menu-top-offset", `${topOffset}px`);
        };

        const setActivePreview = (previewId, options = {}) => {
          const allowEmpty = options.allowEmpty === true;
          const fallbackPreviewId = options.fallbackPreviewId || defaultPreviewId;
          const resolvedPreviewId = findPreview(previewId)
            ? previewId
            : !allowEmpty && findPreview(fallbackPreviewId)
              ? fallbackPreviewId
              : "";

          previews.forEach((preview) => {
            preview.classList.toggle(
              "is-active",
              resolvedPreviewId !== "" &&
                preview.dataset.megaPreview === resolvedPreviewId,
            );
          });

          if (previewContainer) {
            previewContainer.classList.toggle(
              "has-active-preview",
              resolvedPreviewId !== "",
            );
          }

          return resolvedPreviewId;
        };

        const setActivePanel = (panelId, options = {}) => {
          const allowEmpty = options.allowEmpty === true;
          const resolvedPanelId = findPanel(panelId)
            ? panelId
            : !allowEmpty && findPanel(defaultPanelId)
              ? defaultPanelId
              : "";

          panels.forEach((panel) => {
            panel.classList.toggle(
              "is-active",
              resolvedPanelId !== "" && panel.dataset.megaPanel === resolvedPanelId,
            );
          });

          syncTertiaryPanelHeight(resolvedPanelId);

          return resolvedPanelId;
        };

        // Point the preview overlay link at the currently active menu item so
        // the preview image navigates to whatever link is active. The overlay
        // stays aria-hidden / non-focusable since it duplicates the visible
        // menu link; it's a pointer convenience only.
        const updatePreviewLink = (sourceItem) => {
          if (!previewLink) {
            return;
          }

          const sourceLink = sourceItem ? sourceItem.querySelector("a") : null;
          const href = sourceLink ? sourceLink.getAttribute("href") : "";

          if (href && href !== "javascript:;") {
            previewLink.setAttribute("href", href);

            const target = sourceLink.getAttribute("target");
            if (target) {
              previewLink.setAttribute("target", target);
            } else {
              previewLink.removeAttribute("target");
            }

            previewLink.classList.add("is-active");
          } else {
            previewLink.setAttribute("href", "javascript:;");
            previewLink.removeAttribute("target");
            previewLink.classList.remove("is-active");
          }
        };

        const syncActiveTertiary = (panelId, previewId) => {
          const activePanel = findPanel(panelId);
          let activeItem = null;

          if (activePanel) {
            const panelItems = Array.from(
              activePanel.querySelectorAll("[data-mega-tertiary-item]"),
            );

            if (panelItems.length) {
              activeItem =
                panelItems.find(
                  (item) =>
                    previewId &&
                    item.dataset.previewTarget &&
                    item.dataset.previewTarget === previewId,
                ) ||
                panelItems.find((item) => item.dataset.isDefault === "true") ||
                null;
            }
          }

          // Third level items highlight on hover/focus only; `activeItem` just
          // tells the preview image and overlay link what to fall back to.
          tertiaryItems.forEach((item) => {
            item.classList.remove("is-active");
          });

          return activeItem;
        };

        const setActiveSecondary = (item, options = {}) => {
          if (!item) {
            return;
          }

          secondaryItems.forEach((secondaryItem) => {
            secondaryItem.classList.toggle("is-active", secondaryItem === item);
          });

          setActiveSubpanel("");
          // Before the panel is measured, as the layout changes its width.
          setQuaternaryLayout(item.dataset.hasQuaternary === "true");

          const activePanelId = setActivePanel(item.dataset.panelTarget || "");
          const activePreviewId = setActivePreview(
            item.dataset.previewTarget || "",
            {
              allowEmpty: false,
              fallbackPreviewId: defaultPreviewId,
            },
          );
          const activeTertiary = syncActiveTertiary(activePanelId, activePreviewId);
          updatePreviewLink(activeTertiary || item);

          if (options.openOnTouch && !canHover()) {
            hydratePreviewImages();
            closeAllMenus(root);
            root.classList.add("is-open");
            setExpanded(true);
          }
        };

        const setActiveTertiary = (item) => {
          if (!item) {
            return;
          }

          tertiaryItems.forEach((tertiaryItem) => {
            tertiaryItem.classList.toggle("is-active", tertiaryItem === item);
          });

          // Opens this item's fourth level column, or closes the open one
          // when the item has no children.
          setActiveSubpanel(item.dataset.subpanelTarget || "");
          updatePreviewLink(item);

          if (item.dataset.previewTarget) {
            setActivePreview(item.dataset.previewTarget, {
              allowEmpty: false,
              fallbackPreviewId: item.dataset.previewTarget,
            });
            return;
          }

          setActivePreview("", {
            allowEmpty: false,
            fallbackPreviewId: defaultPreviewId,
          });
        };

        const setActiveQuaternary = (item) => {
          quaternaryItems.forEach((quaternaryItem) => {
            quaternaryItem.classList.toggle("is-active", quaternaryItem === item);
          });

          updatePreviewLink(item);

          // Without its own image, keep showing the parent third level item's.
          if (item.dataset.previewTarget) {
            setActivePreview(item.dataset.previewTarget);
          }
        };

        // The third level stays closed until a second level item is hovered or
        // focused, so the reset state is "nothing selected" plus the parent's
        // own preview image.
        const resetState = () => {
          secondaryItems.forEach((item) => item.classList.remove("is-active"));
          tertiaryItems.forEach((item) => item.classList.remove("is-active"));

          setMobileLevel(2);
          setActiveSubpanel("");
          setQuaternaryLayout(false);
          setActivePanel("", { allowEmpty: true });
          setActivePreview(defaultPreviewId);
          updatePreviewLink(root);
        };

        const openMenu = () => {
          hydratePreviewImages();
          clearCloseTimer();
          clearCloseAnimationTimer();
          updateMenuOffset();
          closeAllMenus(root, { immediate: false });
          root.classList.remove("is-closing");
          root.classList.add("is-open");
          setExpanded(true);
          syncTertiaryPanelHeight();
          syncHeaderMegaMenuState();
        };

        const keepMenuOpen = () => {
          if (!root.classList.contains("is-open")) {
            return;
          }

          clearCloseTimer();
          updateMenuOffset();
          setExpanded(true);
        };

        const closeMenu = () => {
          clearCloseTimer();
          clearCloseAnimationTimer();

          if (!canHover()) {
            forceClose();
            return;
          }

          if (!root.classList.contains("is-open") && !root.classList.contains("is-closing")) {
            return;
          }

          root.classList.remove("is-open");
          root.classList.add("is-closing");
          setExpanded(false);
          resetState();
          syncTertiaryPanelHeight();
          syncHeaderMegaMenuState();
          closeAnimationTimerId = window.setTimeout(() => {
            root.classList.remove("is-closing");
            closeAnimationTimerId = 0;
            syncHeaderMegaMenuState();
          }, closeAnimationDuration);
        };

        const scheduleClose = () => {
          if (!canHover()) {
            return;
          }

          clearCloseTimer();
          closeTimerId = window.setTimeout(() => {
            closeMenu();
          }, closeDelay);
        };

        root._megaMenuClearCloseTimer = () => {
          clearCloseTimer();
          clearCloseAnimationTimer();
        };
        root._megaMenuClose = closeMenu;
        root._megaMenuForceClose = forceClose;
        root._megaMenuSyncPanelHeights = syncPanelHeights;

        secondaryItems.forEach((item) => {
          item.addEventListener("pointerenter", () => {
            setActiveSecondary(item);
            keepMenuOpen();
          });

          item.addEventListener("focusin", () => {
            setActiveSecondary(item, { openOnTouch: true });
            openMenu();
          });
        });

        secondaryLinks.forEach((link) => {
          link.addEventListener("click", (event) => {
            if (canHover()) {
              return;
            }

            const item = link.closest("[data-mega-secondary-item]");

            if (!item || !item.dataset.panelTarget) {
              return;
            }

            // In the drawer, an item with children drills into them; its own
            // page stays reachable from the parent link atop the next level.
            if (isCompact()) {
              const panel = findPanel(item.dataset.panelTarget);

              if (panel && !panel.classList.contains("is-empty")) {
                event.preventDefault();
                setActiveSecondary(item);
                setMobileLevel(3);
              }

              return;
            }

            // The third level is hidden on touch layouts, so a first tap that
            // only selects an invisible panel would cost the visitor a tap.
            if (
              !tertiaryContainer ||
              window.getComputedStyle(tertiaryContainer).display === "none"
            ) {
              return;
            }

            if (!item.classList.contains("is-active")) {
              event.preventDefault();
              setActiveSecondary(item, { openOnTouch: true });
            }
          });
        });

        tertiaryItems.forEach((item) => {
          item.addEventListener("pointerenter", () => {
            setActiveTertiary(item);
            keepMenuOpen();
          });

          item.addEventListener("focusin", () => {
            setActiveTertiary(item);
            openMenu();
          });
        });

        tertiaryItems.forEach((item) => {
          const link = item.querySelector(".header__mega-tertiary-link");

          if (!link || !item.dataset.subpanelTarget) {
            return;
          }

          link.addEventListener("click", (event) => {
            if (canHover()) {
              return;
            }

            if (isCompact()) {
              event.preventDefault();
              setActiveTertiary(item);
              setMobileLevel(4);
              return;
            }

            // Touch on the desktop layout: as with the second level, the first
            // tap opens the fourth level column and the second follows the link.
            const subpanel = findSubpanel(item.dataset.subpanelTarget);

            if (subpanel && !subpanel.classList.contains("is-active")) {
              event.preventDefault();
              setActiveTertiary(item);
            }
          });
        });

        quaternaryItems.forEach((item) => {
          item.addEventListener("pointerenter", () => {
            setActiveQuaternary(item);
            keepMenuOpen();
          });

          item.addEventListener("focusin", () => {
            setActiveQuaternary(item);
            openMenu();
          });
        });

        if (topLink) {
          topLink.addEventListener("pointerenter", () => {
            if (!canHover()) {
              return;
            }

            openMenu();
          });

          topLink.addEventListener("click", (event) => {
            if (canHover()) {
              return;
            }

            event.preventDefault();

            if (root.classList.contains("is-open")) {
              forceClose();
            } else {
              openMenu();
              resetState();
            }
          });
        }

        megaMenu.addEventListener("pointerenter", () => {
          if (canHover()) {
            keepMenuOpen();
          }
        });

        headerInner?.addEventListener("pointerenter", () => {
          keepMenuOpen();
        });

        headerInner?.addEventListener("pointerleave", () => {
          if (!canHover()) {
            return;
          }

          if (root.classList.contains("is-open")) {
            scheduleClose();
          }
        });

        root.addEventListener("pointerleave", (event) => {
          if (!canHover()) {
            return;
          }

          if (event.relatedTarget && root.contains(event.relatedTarget)) {
            return;
          }

          if (root.classList.contains("is-open")) {
            scheduleClose();
          }
        });

        root.addEventListener("focusin", () => {
          if (canHover()) {
            openMenu();
          }
        });

        root.addEventListener("focusout", (event) => {
          if (!canHover()) {
            return;
          }

          if (root.contains(event.relatedTarget)) {
            return;
          }

          closeMenu();
        });

        root.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            closeMenu();
            topLink?.focus();
          }
        });

        const backBtn = megaMenu.querySelector(".header__mega-back");

        if (backBtn) {
          // Steps back up the drill-down one level at a time, and only closes
          // the menu from the second level.
          backBtn.addEventListener("click", () => {
            const level = getMobileLevel();

            if (level === 4) {
              setActiveSubpanel("");
              setMobileLevel(3);
            } else if (level === 3) {
              setMobileLevel(2);
            } else {
              forceClose();
            }
          });
        }

        updateMenuOffset();
        resetState();
        syncPanelHeights();
      });

      nonMegaTopLevelItems.forEach((item) => {
        item.addEventListener("pointerenter", () => {
          if (!canHover()) {
            return;
          }

          closeAllMenus(null, { immediate: false });
        });

        item.addEventListener("focusin", () => {
          if (!canHover()) {
            return;
          }

          closeAllMenus();
        });
      });

      document.addEventListener("click", (event) => {
        if (canHover()) {
          return;
        }

        if (event.target.closest(".header__menu-item--mega")) {
          return;
        }

        closeAllMenus();
      });

      window.addEventListener(
        "resize",
        () => {
          megaRoots.forEach((root) => {
            const megaMenu = root.querySelector("[data-mega-menu]");

            if (megaMenu && headerInner && menuPositionContext && canHover()) {
              const contextRect = menuPositionContext.getBoundingClientRect();
              const headerInnerRect = headerInner.getBoundingClientRect();
              const topOffset = Math.max(
                0,
                Math.round(headerInnerRect.bottom - contextRect.top),
              );

              megaMenu.style.setProperty(
                "--mega-menu-top-offset",
                `${topOffset}px`,
              );
            } else if (megaMenu) {
              megaMenu.style.removeProperty("--mega-menu-top-offset");
            }

            // A full reset, so a pinned column width can't outlive the resize.
            if (canHover() && typeof root._megaMenuForceClose === "function") {
              root._megaMenuForceClose();
            }

            if (typeof root._megaMenuSyncPanelHeights === "function") {
              root._megaMenuSyncPanelHeights();
            }

            const topLink = getTopLink(root);

            if (topLink) {
              topLink.setAttribute(
                "aria-expanded",
                canHover() ? "false" : topLink.getAttribute("aria-expanded") || "false",
              );
            }
          });

          syncHeaderMegaMenuState();
        },
        { passive: true },
      );

      syncHeaderMegaMenuState();
    }

    initHeaderMegaMenus();

    function initCtaBannerForms() {
      const triggers = document.querySelectorAll("[data-cta-banner-form-trigger]");
      let hubSpotFormsApiPromise = null;

      const loadHubSpotFormsApi = () => {
        if (window.hbspt?.forms?.create) {
          return Promise.resolve(window.hbspt);
        }

        if (hubSpotFormsApiPromise) {
          return hubSpotFormsApiPromise;
        }

        hubSpotFormsApiPromise = new Promise((resolve, reject) => {
          const existingScript = document.querySelector(
            'script[src*="hsforms.net/forms/embed/v2.js"]',
          );
          const script = existingScript || document.createElement("script");

          const handleLoad = () => {
            if (window.hbspt?.forms?.create) {
              resolve(window.hbspt);
            } else {
              reject(new Error("HubSpot forms API did not initialize"));
            }
          };

          script.addEventListener("load", handleLoad, { once: true });
          script.addEventListener("error", reject, { once: true });

          if (!existingScript) {
            script.src = "https://js-eu1.hsforms.net/forms/embed/v2.js";
            script.defer = true;
            script.dataset.v1HubspotFormsApi = "true";
            document.head.appendChild(script);
          }
        });

        return hubSpotFormsApiPromise;
      };

      const initializeDeferredHubSpotForm = (form) => {
        const target = form.querySelector("[data-v1-hubspot-form]");

        if (!target || target.dataset.initialized === "true") {
          return;
        }

        target.dataset.initialized = "true";

        loadHubSpotFormsApi()
          .then((hbspt) => {
            target.querySelector("[data-v1-hubspot-loading]")?.remove();
            hbspt.forms.create({
              portalId: target.dataset.portalId,
              formId: target.dataset.formId,
              region: target.dataset.region || "eu1",
              target: `#${CSS.escape(target.id)}`,
            });
          })
          .catch(() => {
            target.dataset.initialized = "false";
            const loadingMessage = target.querySelector(
              "[data-v1-hubspot-loading]",
            );

            if (loadingMessage) {
              loadingMessage.textContent =
                "The form could not be loaded. Please try again.";
            }
          });
      };

      triggers.forEach((trigger) => {
        const banner = trigger.closest(".cta-banner");
        const main = banner?.querySelector("[data-cta-banner-main]");
        const form = banner?.querySelector("[data-cta-banner-form]");

        if (!banner || !main || !form) {
          return;
        }

        const focusForm = () => {
          const focusTarget =
            form.querySelector(
              'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])',
            ) || form;

          try {
            focusTarget.focus({ preventScroll: true });
          } catch (error) {
            focusTarget.focus();
          }
        };

        const resetAnimatedStyles = (element) => {
          element.style.maxHeight = "";
          element.style.opacity = "";
          element.style.overflow = "";
          element.style.transform = "";
          element.style.willChange = "";
        };

        const finishAnimation = (animation, element) => {
          resetAnimatedStyles(element);
          animation.cancel();
        };

        const completeSwap = () => {
          main.hidden = true;
          form.hidden = false;
          banner.classList.add("is-showing-form");
          banner.classList.remove("is-transitioning-form");
          trigger.setAttribute("aria-expanded", "true");
          resetAnimatedStyles(main);
          resetAnimatedStyles(form);
          focusForm();
        };

        trigger.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();

          if (
            banner.classList.contains("is-showing-form") ||
            banner.classList.contains("is-transitioning-form")
          ) {
            return;
          }

          initializeDeferredHubSpotForm(form);

          const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
          ).matches;
          const canAnimate =
            !prefersReducedMotion &&
            typeof main.animate === "function" &&
            typeof form.animate === "function";

          if (!canAnimate) {
            completeSwap();
            return;
          }

          banner.classList.add("is-transitioning-form");

          const mainHeight = main.offsetHeight;
          main.style.maxHeight = `${mainHeight}px`;
          main.style.overflow = "hidden";
          main.style.willChange = "max-height, opacity, transform";

          const mainAnimation = main.animate(
            [
              {
                maxHeight: `${mainHeight}px`,
                opacity: 1,
                transform: "translateY(0)",
              },
              {
                maxHeight: "0px",
                opacity: 0,
                transform: "translateY(-2rem)",
              },
            ],
            {
              duration: 360,
              easing: "cubic-bezier(0.4, 0, 0.2, 1)",
              fill: "forwards",
            },
          );

          mainAnimation.finished
            .then(() => {
              main.hidden = true;
              finishAnimation(mainAnimation, main);

              form.hidden = false;
              banner.classList.add("is-showing-form");
              trigger.setAttribute("aria-expanded", "true");

              form.style.maxHeight = "0px";
              form.style.opacity = "0";
              form.style.overflow = "hidden";
              form.style.transform = "translateY(2rem)";
              form.style.willChange = "max-height, opacity, transform";

              const formHeight = form.scrollHeight;
              const formAnimation = form.animate(
                [
                  {
                    maxHeight: "0px",
                    opacity: 0,
                    transform: "translateY(2rem)",
                  },
                  {
                    maxHeight: `${formHeight}px`,
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                ],
                {
                  duration: 460,
                  easing: "cubic-bezier(0.16, 1, 0.3, 1)",
                  fill: "forwards",
                },
              );

              formAnimation.finished
                .then(() => {
                  banner.classList.remove("is-transitioning-form");
                  finishAnimation(formAnimation, form);
                  focusForm();
                })
                .catch(completeSwap);
            })
            .catch(completeSwap);
        });
      });
    }

    initCtaBannerForms();

    // --- GSAP ScrollTrigger animations ---
    if (typeof gsap !== "undefined") {
      gsap.registerPlugin(ScrollTrigger);

      // Connect Lenis to ScrollTrigger so scrub/pin positions stay in sync
      if (lenis) {
        lenis.on("scroll", ScrollTrigger.update);
      }

      // Parallax effects
      gsap.utils.toArray("[data-speed]").forEach((element) => {
        const speed = parseFloat(element.dataset.speed) || 0.5;
        gsap.to(element, {
          yPercent: -50 * speed,
          ease: "none",
          scrollTrigger: {
            trigger: element,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.5,
          },
        });
      });

      // Soft float effect
      gsap.utils.toArray("[data-float]").forEach((element) => {
        const intensity = parseFloat(element.dataset.float) || 20;
        gsap.to(element, {
          y: `random(-${intensity}, ${intensity})`,
          rotation: `random(-2, 2)`,
          duration: `random(3, 5)`,
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          delay: `random(0, 2)`,
        });
      });

      // Scroll-triggered reveal animations
      gsap.utils.toArray("[data-reveal]").forEach((element) => {
        gsap.fromTo(
          element,
          {
            opacity: 0,
            y: 50,
            scale: 0.98,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: element,
              start: "top 85%",
              end: "bottom 15%",
              toggleActions: "play none none reverse",
            },
          },
        );
      });

      // CTA Banner — marker fall-down after AOS reveals the map
      const ctaMap = document.querySelector(".cta-banner__map");
      if (ctaMap) {
        const markers = ctaMap.querySelectorAll(".cta-banner__marker");

        // Hide markers initially
        gsap.set(markers, {
          opacity: 0,
          xPercent: -50,
          yPercent: -100,
          y: -50,
        });

        // Listen for AOS fade-up completion, then drop markers in
        var markersAnimated = false;
        var observer = new MutationObserver(function () {
          if (!markersAnimated && ctaMap.classList.contains("aos-animate")) {
            markersAnimated = true;
            observer.disconnect();
            dropMarkers();
          }
        });
        observer.observe(ctaMap, { attributes: true, attributeFilter: ["class"] });

        function dropMarkers() {
          gsap.to(markers, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "bounce.out",
            stagger: 0.15,
            onComplete: function () {
              markers.forEach(function (m) {
                m.classList.add("marker-visible");
              });
            },
          });
        }
      }

      // Smooth anchor link scrolling
      let cachedHeaderHeight = 0;

      function updateCachedHeaderHeight() {
        const h = document.querySelector("header");
        if (h) cachedHeaderHeight = h.offsetHeight;
      }

      requestAnimationFrame(updateCachedHeaderHeight);
      window.addEventListener("resize", () => {
        requestAnimationFrame(updateCachedHeaderHeight);
      });

      document
        .querySelectorAll('a[href^="#"]:not([data-cta-banner-form-trigger])')
        .forEach((anchor) => {
          anchor.addEventListener("click", function (e) {
            e.preventDefault();
            const targetId = this.getAttribute("href");
            const targetElement = document.querySelector(targetId);

            if (targetElement) {
              const targetPosition =
                targetElement.offsetTop - cachedHeaderHeight - 20;

              if (lenis) {
                lenis.scrollTo(targetPosition, { duration: 1.5 });
              } else {
                gsap.to(window, {
                  duration: 1.5,
                  scrollTo: { y: targetPosition, autoKill: false },
                  ease: "power2.inOut",
                });
              }
            }
          });
        });
    } else {
      console.warn("GSAP not loaded - ScrollTrigger animations disabled");
    }

    // --- Event/webinar banner form width variable ---
    const bannerForm = document.querySelector(".page-banner__form");

    if (bannerForm) {
      const updateBannerFormWidth = () => {
        if (window.innerWidth >= 992) {
          document.documentElement.style.setProperty(
            "--banner-form-width",
            `${bannerForm.offsetWidth}px`,
          );
        } else {
          document.documentElement.style.removeProperty("--banner-form-width");
        }
      };

      updateBannerFormWidth();
      window.addEventListener("resize", updateBannerFormWidth, { passive: true });
      window.addEventListener("load", () => {
        requestAnimationFrame(updateBannerFormWidth);
      }, { passive: true });
    }

    // --- Mobile menu toggle ---
    const mobileToggle = document.querySelectorAll('.header__menu-toggle');

    if (mobileToggle.length)
    {
      mobileToggle.forEach((toggle) =>
      {
        toggle.addEventListener('click', function()
        {
          document.body.classList.toggle('show-mobile-menu');
        });
      });
    }

    // --- Header search palette ---
    const searchPalette = document.getElementById('header-search-palette');

    if (searchPalette) {
      const searchButtons = document.querySelectorAll('.header__search-btn');
      const searchPanel = searchPalette.querySelector('.header__search-palette-panel');
      const searchInput = searchPalette.querySelector('.header__search-palette-input');
      const searchClose = searchPalette.querySelector('.header__search-palette-close');
      const searchBackdrop = searchPalette.querySelector('.header__search-palette-backdrop');
      let activeSearchTrigger = null;

      function preventLenisOnAlgoliaDropdowns() {
        document.querySelectorAll('.algolia-autocomplete, .aa-dropdown-menu').forEach(dropdown => {
          dropdown.setAttribute('data-lenis-prevent', '');
        });
      }

      const algoliaDropdownObserver = new MutationObserver(() => {
        if (!searchPalette.hidden) {
          preventLenisOnAlgoliaDropdowns();
        }
      });

      algoliaDropdownObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      function openSearchPalette(trigger) {
        activeSearchTrigger = trigger || null;
        searchPalette.hidden = false;
        document.documentElement.classList.add('header-search-is-open');
        document.body.classList.add('header-search-is-open');
        document.body.classList.remove('show-mobile-menu');
        lenis?.stop();
        preventLenisOnAlgoliaDropdowns();
        searchButtons.forEach(button => button.setAttribute('aria-expanded', 'true'));

        requestAnimationFrame(() => {
          preventLenisOnAlgoliaDropdowns();
          searchInput?.focus();
        });
      }

      function closeSearchPalette() {
        searchPalette.hidden = true;
        document.documentElement.classList.remove('header-search-is-open');
        document.body.classList.remove('header-search-is-open');
        lenis?.start();
        searchButtons.forEach(button => button.setAttribute('aria-expanded', 'false'));
        activeSearchTrigger?.focus();
        activeSearchTrigger = null;
      }

      searchButtons.forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          openSearchPalette(button);
        });
      });

      searchClose?.addEventListener('click', closeSearchPalette);
      searchBackdrop?.addEventListener('click', closeSearchPalette);

      document.addEventListener('keydown', event => {
        if (searchPalette.hidden || event.key !== 'Escape') return;
        closeSearchPalette();
      });

      searchPalette.addEventListener('click', event => {
        if (!searchPanel || searchPanel.contains(event.target)) return;
        closeSearchPalette();
      });
    }

    // --- Knowledge Hub ---
    const hub = document.querySelector('.knowledge-hub');

    if (hub) {
      const searchInput   = hub.querySelector('.search-form__input');
      const searchForm    = hub.querySelector('.search-form');
      const pillInputs    = Array.from(hub.querySelectorAll('.knowledge-hub__filters__pills input[name="post_type"]'));
      const allInput      = hub.querySelector('.knowledge-hub__filters__pills input[value="all"]');
      const allTypesData  = window.knowledgeHubData && Array.isArray(window.knowledgeHubData.postTypes) ? window.knowledgeHubData.postTypes : [];
      const ALL_TYPES     = allTypesData.length ? allTypesData : ['post', 'news', 'case-studies', 'events', 'webinars', 'assets', 'reports'];
      const currentType   = window.knowledgeHubData && window.knowledgeHubData.currentType ? window.knowledgeHubData.currentType : '';
      const currentSearch = searchInput ? searchInput.value.trim() : '';
      const useNativeSearch = window.knowledgeHubData && window.knowledgeHubData.useNativeSearch;

      const state = {
        currentType,
        postTypes : currentType ? [currentType] : [...ALL_TYPES],
        search    : currentSearch,
      };

      // Each grid pages on its own. The hub has a single grid; the events
      // archive has one for upcoming and one for previous events, which the
      // search refreshes together.
      const feeds = Array.from(hub.querySelectorAll('.knowledge-hub__grid')).map(grid => ({
        grid,
        moreWrap    : grid.parentElement.querySelector(':scope > .knowledge-hub__more'),
        eventStatus : grid.dataset.eventStatus || '',
        page        : 1,
        loading     : false,
        hasMore     : grid.dataset.hasMore === 'true',
      }));

      function syncMoreButton(feed) {
        if (feed.moreWrap) feed.moreWrap.style.display = feed.hasMore ? '' : 'none';
      }

      function fetchPosts(feed, reset) {
        if (feed.loading) return;
        feed.loading = true;

        if (reset) {
          feed.page = 1;
        } else {
          feed.page++;
        }

        const body = new FormData();
        body.append('action',     'knowledge_hub_query');
        body.append('post_types', JSON.stringify(state.postTypes));
        body.append('search',     state.search);
        body.append('page',       feed.page);
        body.append('current_type', state.currentType);
        body.append('event_status', feed.eventStatus);

        if (reset) feed.grid.classList.add('is-loading');

        fetch(ajax_params.ajax_url, { method: 'POST', body, credentials: 'same-origin' })
          .then(r => r.json())
          .then(data => {
            if (!data.success) return;
            if (reset) {
              feed.grid.innerHTML = data.data.html;
            } else {
              feed.grid.insertAdjacentHTML('beforeend', data.data.html);
            }
            feed.hasMore = data.data.has_more;
            syncMoreButton(feed);
          })
          .finally(() => {
            feed.loading = false;
            feed.grid.classList.remove('is-loading');
          });
      }

      function refreshFeeds() {
        feeds.forEach(feed => fetchPosts(feed, true));
      }

      // Pill filters
      pillInputs.forEach(input => {
        if (input.value === 'all') return;

        input.addEventListener('change', () => {
          const checked = pillInputs
            .filter(i => i.value !== 'all' && i.checked)
            .flatMap(i => i.value.split(','));

          if (checked.length === 0) {
            allInput.checked = true;
            state.postTypes  = [...ALL_TYPES];
          } else {
            allInput.checked = false;
            state.postTypes  = checked;
          }

          refreshFeeds();
        });
      });

      if (allInput) {
        allInput.addEventListener('change', () => {
          if (allInput.checked) {
            pillInputs.forEach(i => { if (i.value !== 'all') i.checked = false; });
            state.postTypes = [...ALL_TYPES];
            refreshFeeds();
          } else {
            // Prevent unchecking All when nothing specific is selected
            allInput.checked = true;
          }
        });
      }

      // Search — debounced input + prevent native form submit
      let searchTimer;
      if (!useNativeSearch) {
        searchForm?.addEventListener('submit', e => e.preventDefault());
        searchInput?.addEventListener('input', () => {
          clearTimeout(searchTimer);
          searchTimer = setTimeout(() => {
            state.search = searchInput.value.trim();
            refreshFeeds();
          }, 400);
        });
      }

      // Load more
      feeds.forEach(feed => {
        feed.moreWrap?.querySelector('a, button')?.addEventListener('click', e => {
          e.preventDefault();
          if (!feed.loading && feed.hasMore) fetchPosts(feed, false);
        });
      });
    }

    // --- Social share: copy link (e.g. Instagram, which has no web share URL) ---
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-share-copy]');
      if (!trigger) return;

      event.preventDefault();

      const url = trigger.getAttribute('data-share-copy') || window.location.href;
      const flagCopied = () => {
        trigger.classList.add('is-copied');
        setTimeout(() => trigger.classList.remove('is-copied'), 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(flagCopied).catch(() => {});
      } else {
        const helper = document.createElement('textarea');
        helper.value = url;
        helper.setAttribute('readonly', '');
        helper.style.position = 'absolute';
        helper.style.left = '-9999px';
        document.body.appendChild(helper);
        helper.select();
        try { document.execCommand('copy'); flagCopied(); } catch (e) {}
        document.body.removeChild(helper);
      }
    });
  });

  jQuery(document).ready(function ($) {
    const selectOptions = {
      minimumResultsForSearch: Infinity,
      placeholder: "Select",
    };

    // Select2 marks an element it owns with data-select2-id on init and clears
    // it on destroy. Track initialisation through that rather than the
    // select2-hidden-accessible class: HubSpot re-renders its fields with
    // React, which rewrites class on the <select> and drops the class while
    // the Select2 instance is still very much alive.
    const skipSelector = "[data-no-select2], [data-select2-id]";

    // Define your SVG HTML as a string for a downward facing chevron
    var customSvgArrow = `
    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="9.657" height="5.458" viewBox="0 0 9.657 5.458">
      <defs>
        <clipPath id="clip-path">
          <rect id="Rectangle_245" data-name="Rectangle 245" width="9.657" height="5.458" fill="currentColor"/>
        </clipPath>
      </defs>
      <g id="Group_38135" data-name="Group 38135" transform="translate(9.657 5.458) rotate(180)">
        <g id="Group_932" data-name="Group_932" clip-path="url(#clip-path)">
          <path id="Path_273" data-name="Path 273" d="M1.075,5.274,4.829,1.52,8.582,5.274a.63.63,0,1,0,.891-.891l-4.2-4.2a.631.631,0,0,0-.891,0l-4.2,4.2a.63.63,0,0,0,.891.891" transform="translate(0 0)" fill="currentColor"/>
        </g>
      </g>
    </svg>
  `;

    // Select2 draws its own arrow; the design calls for this chevron instead.
    // Guarded so re-running over an existing box never stacks a second copy.
    const appendArrow = (selection) => {
      if (!selection || selection.querySelector("[data-select2-arrow]")) {
        return;
      }

      var wrapper = document.createElement("div");
      wrapper.dataset.select2Arrow = "true";
      wrapper.innerHTML = customSvgArrow;
      wrapper.style.position = "absolute";
      wrapper.style.top = "50%";
      wrapper.style.right = "12px";
      wrapper.style.transform = "translateY(-50%)";
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
      wrapper.style.justifyContent = "center";

      // Ensures the custom arrow is correctly positioned within Select2
      selection.style.position = "relative";
      selection.appendChild(wrapper);
    };

    const initSelects = () => {
      // A Select2 box whose <select> was swapped out from under it (an embedded
      // form re-rendering a field) is left orphaned in the DOM, which is what
      // shows up as a duplicate dropdown. Clear those before initialising.
      $(".select2-container").each(function () {
        const $box = $(this);

        // The open dropdown is parked on <body> and owns no <select>.
        if ($box.children(".select2-dropdown").length) {
          return;
        }

        if (!$box.prev("select[data-select2-id]").length) {
          $box.remove();
        }
      });

      const selects = $("select").filter(function () {
        return (
          !$(this).is(skipSelector) &&
          !$(this).closest(".select2-container").length
        );
      });

      if (!selects.length) {
        return;
      }

      loadSelect2()
        .then(() => {
          selects.each(function () {
            const $select = $(this);

            // The list was captured before the Select2 chunk resolved, so the
            // DOM may have moved on: skip anything since detached or already
            // initialised rather than tearing a live box down and rebuilding it.
            if (!this.isConnected || this.hasAttribute("data-select2-id")) {
              return;
            }

            $select.siblings(".select2-container").remove();
            $select.select2(selectOptions);

            appendArrow(
              $select
                .data("select2")
                ?.$container?.[0]?.querySelector(
                  ".select2-selection--single, .select2-selection--multiple",
                ),
            );
          });
        })
        .catch(() => {
          // Select2 could not be loaded; the native selects stay usable.
        });
    };

    // Let Lenis ignore scroll events inside Select2 dropdowns
    $(document).on("select2:open", () => {
      const dropdown = document.querySelector(".select2-dropdown");
      if (dropdown) dropdown.setAttribute("data-lenis-prevent", "");
    });

    initSelects();

    // Embedded forms (the HubSpot form in the contact page banner, for example)
    // inject their markup asynchronously, so a single pass at DOM ready races
    // them and often leaves an unstyled native select behind.
    if (typeof MutationObserver !== "undefined") {
      let scheduled = 0;

      const observer = new MutationObserver((mutations) => {
        const addedSelect = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.("select") || node.querySelector?.("select")),
          ),
        );

        if (!addedSelect) {
          return;
        }

        cancelAnimationFrame(scheduled);
        scheduled = requestAnimationFrame(initSelects);
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  });

  jQuery(document).ready(function ($) {
    const hash = window.location.hash;

    if (!hash) {
      return;
    }

    const $targetElement = $(hash);

    if ($targetElement.length) {
      const offset = -5 * 16;
      const targetPosition = $targetElement.offset().top + offset;
      $("html, body").animate({ scrollTop: targetPosition }, 800);
    }
  });

  const initializeScrollAnimations = () => {
    const animatedElements = document.querySelectorAll("[data-aos]");

    const revealWithoutAnimations = () => {
      animatedElements.forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith("data-aos")) {
            element.removeAttribute(attribute.name);
          }
        });
      });
    };

    if (!animatedElements.length) {
      return;
    }

    if (
      window.matchMedia("(max-width: 767px)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      revealWithoutAnimations();
      return;
    }

    import("aos")
      .then(({ default: AOS }) => {
        AOS.init({
          duration: 700,
          once: true,
        });

        window.AOS = AOS;

        // Fonts, embedded forms and column widths can change after AOS caches
        // its offsets. Refresh against the resulting layout, not just resize.
        let refreshFrame = 0;
        const refreshPositions = () => {
          cancelAnimationFrame(refreshFrame);
          refreshFrame = requestAnimationFrame(() => AOS.refresh());
        };

        window.addEventListener("load", refreshPositions, { once: true });
        if (document.fonts) document.fonts.ready.then(refreshPositions);
        if (typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver(refreshPositions);
          observer.observe(document.querySelector("main") || document.body);
        }
      })
      .catch(revealWithoutAnimations);
  };

  whenDOMReady(initializeScrollAnimations);
}
